import { Suspense, useState } from "react";
import { useGetEditSource } from "@/api/attachment";
import { lazyWithReload } from "@/lib/lazy-with-reload";
import { retouchImage } from "@/api/image";
import { buildRetouchRequest } from "@/components/images/image-studio/retouch-request";
import type { ImageStudioSavePayload } from "@/components/images/image-studio/image-studio";
import type { ImageStudioLabels } from "@/components/images/image-studio/labels";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { tWithKey } from "@/lib/i18n-fallback";
import type { Attachment } from "@/lib/types";
import { useTranslation } from "react-i18next";

const ImageStudio = lazyWithReload(() =>
  import("@/components/images/image-studio/image-studio").then((mod) => ({
    default: mod.ImageStudio,
  })),
);

function studioLabels(
  t: (key: string, options?: { defaultValue?: string }) => string,
): ImageStudioLabels {
  return {
    title: tWithKey(t, "imageStudio.title", "Retoucher l'image"),
    save: tWithKey(t, "imageStudio.save", "Enregistrer"),
    cancel: tWithKey(t, "imageStudio.cancel", "Annuler"),
    reset: tWithKey(t, "imageStudio.reset", "Réinitialiser"),
    close: tWithKey(t, "imageStudio.close", "Fermer"),

    contrast: tWithKey(t, "imageStudio.contrast", "Contraste"),
    luminosity: tWithKey(t, "imageStudio.luminosity", "Luminosité"),
    sharpen: tWithKey(t, "imageStudio.sharpen", "Netteté"),

    rotate: tWithKey(t, "imageStudio.rotate", "Rotation"),
    rotateLeft: tWithKey(t, "imageStudio.rotateLeft", "90° gauche"),
    rotateRight: tWithKey(t, "imageStudio.rotateRight", "90° droite"),

    perspectiveActivate: tWithKey(
      t,
      "imageStudio.perspective.activate",
      "Déformer les 4 coins",
    ),
    perspectiveValidate: tWithKey(
      t,
      "imageStudio.perspective.validate",
      "Valider la déformation",
    ),
    perspectiveReset: tWithKey(
      t,
      "imageStudio.perspective.reset",
      "Réinitialiser la déformation",
    ),
    perspectiveHint: tWithKey(
      t,
      "imageStudio.perspective.hint",
      "Déplacez les 4 coins : l'image se déforme en temps réel.",
    ),
    grid: tWithKey(t, "imageStudio.grid", "Grille"),

    symmetries: tWithKey(t, "imageStudio.symmetries", "Symétries"),
    flipH: tWithKey(t, "imageStudio.flipH", "Miroir H"),
    flipV: tWithKey(t, "imageStudio.flipV", "Miroir V"),

    cropActivate: tWithKey(t, "imageStudio.crop.activate", "Recadrer"),
    cropValidate: tWithKey(t, "imageStudio.crop.validate", "Valider le recadrage"),
    cropReset: tWithKey(t, "imageStudio.crop.reset", "Réinitialiser le recadrage"),
    cropHint: tWithKey(
      t,
      "imageStudio.crop.hint",
      "Glissez le rectangle et ses poignées pour recadrer.",
    ),

    zoom: tWithKey(t, "imageStudio.zoom", "Zoom"),

    toolPerspective: tWithKey(t, "imageStudio.tool.perspective", "Déformation"),
    toolRotate: tWithKey(t, "imageStudio.tool.rotate", "Rotation"),
    toolCrop: tWithKey(t, "imageStudio.tool.crop", "Recadrage"),
    toolAdjust: tWithKey(t, "imageStudio.tool.adjust", "Réglages"),

    nonHdHint: tWithKey(
      t,
      "imageStudio.nonHdHint",
      "Image non-HD : la retouche part du dérivé 1600 px, pas d'un original pleine résolution.",
    ),
    loadError: tWithKey(
      t,
      "imageStudio.loadError",
      "Impossible de charger l'image source.",
    ),
    saving: tWithKey(t, "imageStudio.saving", "Enregistrement…"),
    loading: tWithKey(t, "imageStudio.loading", "Chargement…"),
    serverResult: tWithKey(t, "imageStudio.serverResult", "Résultat serveur"),
  };
}

type ImageStudioHostProps = {
  attachment: Attachment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function ImageStudioHost({
  attachment,
  open,
  onOpenChange,
  onSaved,
}: ImageStudioHostProps) {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const [saving, setSaving] = useState(false);
  const labels = studioLabels(t);

  const { data: source, isError: sourceError } = useGetEditSource(
    attachment?.id,
    open && !!attachment?.id,
  );

  const handleSave = async (payload: ImageStudioSavePayload) => {
    if (!attachment?.id || !source || !instanceData) {
      throw new Error("missingRetouchTarget");
    }
    setSaving(true);
    try {
      await retouchImage({
        ...buildRetouchRequest({
          attachmentId: attachment.id,
          link: source.link || attachment.link || "",
          isHd: source.isHd,
          savedRecipe: payload.isFirstEdit ? null : attachment.editRecipe,
          recipe: payload.recipe,
          applyWatermark: attachment.applyWatermark,
        }),
        s3Bucket: instanceData.s3Bucket,
        instanceId: instanceData.id,
      });
      toast({
        variant: "success",
        description: tWithKey(
          t,
          "imageStudio.saveSuccess",
          "Image retouchée.",
        ),
      });
      // La retouche a régénéré les dérivés côté serveur : on rafraîchit la
      // fiche et on ferme la modal (le parent recharge l'image mise à jour).
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast({
        variant: "error",
        title: t("error"),
        description: tWithKey(
          t,
          "imageStudio.saveError",
          "La retouche a échoué.",
        ),
      });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[94vh] max-h-[94vh] w-[97vw] max-w-[1600px] flex-col overflow-hidden p-4 sm:p-6"
        preventOutsideClose
        stacked
      >
        <DialogTitle className="sr-only">{labels.title}</DialogTitle>
        {sourceError && (
          <p className="text-sm text-red-600">{labels.loadError}</p>
        )}
        {source?.url && attachment?.id && (
          <Suspense
            fallback={
              <p className="text-sm text-muted-foreground">{labels.loading}</p>
            }
          >
            <div className="flex min-h-0 flex-1 flex-col">
            <ImageStudio
              imageUrl={source.url}
              // Quand la source a dû retomber sur `-large.webp` (source figée
              // absente), l'API renvoie isFirstEdit=true : les retouches sont
              // déjà cuites dans l'image chargée, on repart donc d'une recette
              // neutre pour ne pas les ré-appliquer par-dessus.
              savedRecipe={source.isFirstEdit ? null : attachment.editRecipe}
              identity={attachment.id}
              isHd={source.isHd}
              saving={saving}
              labels={labels}
              onSave={handleSave}
              onCancel={() => onOpenChange(false)}
            />
            </div>
          </Suspense>
        )}
      </DialogContent>
    </Dialog>
  );
}
